import { useEffect, useState } from 'react';
import { FaGlobe, FaSpinner } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { socialService } from '../../services/apiService';
import LinkifiedText from '../LinkifiedText';
import './PostTranslateControl.css';

function shouldOfferTranslation(post, siteLanguage) {
  const content = String(post?.content || '').trim();
  if (!content) return false;
  const postLang = post?.language || 'und';
  if (postLang === siteLanguage) return false;
  return true;
}

export default function PostContentWithTranslate({ post, className = 'social-post-content' }) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [mode, setMode] = useState('original'); // original | translated
  const [translated, setTranslated] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setMode('original');
    setTranslated('');
    setError('');
  }, [post?.id, post?.content, language]);

  if (!post?.content) return null;

  const offer = shouldOfferTranslation(post, language);
  const displayText = mode === 'translated' && translated ? translated : post.content;

  const handleTranslate = async () => {
    if (mode === 'translated') {
      setMode('original');
      return;
    }

    if (translated) {
      setMode('translated');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await socialService.translatePost(post.id, language);
      const next = res?.data?.translation?.content || '';
      if (!next) throw new Error(t('translate.failed'));
      setTranslated(next);
      setMode('translated');
    } catch (err) {
      setError(err.message || t('translate.failed'));
    } finally {
      setLoading(false);
    }
  };

  const actionLabel = (() => {
    if (loading) return t('translate.translating');
    if (mode === 'translated') return t('translate.showOriginal');
    if (translated) return t('translate.showTranslation');
    return language === 'en' ? t('translate.toEnglish') : t('translate.toPortuguese');
  })();

  return (
    <div className="post-translate-block">
      <LinkifiedText text={displayText} className={className} />
      {offer && (
        <div className="post-translate-actions">
          <button
            type="button"
            className={`post-translate-btn${mode === 'translated' ? ' is-active' : ''}`}
            onClick={handleTranslate}
            disabled={loading}
          >
            {loading ? <FaSpinner className="social-spin" aria-hidden="true" /> : <FaGlobe aria-hidden="true" />}
            <span>{actionLabel}</span>
          </button>
          {error && <span className="post-translate-error">{error}</span>}
        </div>
      )}
    </div>
  );
}
